import { useCallback, useEffect, useRef, useState } from 'react'

export interface UseImageUploadOptions {
	/**
	 * URL of an already-persisted image (Edit modals). When set and the user
	 * removes the image, `deleteImage` flips true so the caller can signal a
	 * server-side delete. Pass null/undefined for Add modals.
	 */
	existingImageUrl?: string | null
	/**
	 * When this flips from false to true the picker repopulates: the local
	 * file is cleared and the existing image (if any) is restored. Mirrors the
	 * modals' useEffect-on-isOpen behavior. Omit for always-on usage.
	 */
	isOpen?: boolean
}

export interface UseImageUpload {
	/** The freshly-picked file, if any. */
	imageFile: File | null
	/** Object URL for a freshly-picked file, else the existing image URL. */
	previewUrl: string | null
	/** True once a previously-persisted image has been removed. */
	deleteImage: boolean
	/** Lightbox overlay flag. */
	showOverlay: boolean
	setShowOverlay: (show: boolean) => void
	/** Ref to wire onto a hidden `<input type="file">`. */
	fileInputRef: React.RefObject<HTMLInputElement>
	handleImageSelect: (e: React.ChangeEvent<HTMLInputElement>) => void
	handleImageRemove: () => void
}

/**
 * Single-image upload module: owns the picked file, its object URL (with
 * revoke-on-replace and revoke-on-unmount cleanup), the existing-image /
 * delete-flag bookkeeping for Edit modals, and the lightbox overlay flag.
 *
 * One implementation backs the Add/Edit Expense and Add PayUp receipt flows
 * that each used to hand-roll the same file/preview/overlay state.
 */
export const useImageUpload = ({
	existingImageUrl = null,
	isOpen,
}: UseImageUploadOptions = {}): UseImageUpload => {
	const [imageFile, setImageFile] = useState<File | null>(null)
	const [localPreviewUrl, setLocalPreviewUrl] = useState<string | null>(null)
	const [existingUrl, setExistingUrl] = useState<string | null>(existingImageUrl)
	const [deleteImage, setDeleteImage] = useState(false)
	const [showOverlay, setShowOverlay] = useState(false)
	const fileInputRef = useRef<HTMLInputElement>(null)

	// Track the live object URL so the unmount cleanup can revoke it without
	// re-subscribing on every URL change.
	const localPreviewUrlRef = useRef<string | null>(null)
	useEffect(() => {
		localPreviewUrlRef.current = localPreviewUrl
	}, [localPreviewUrl])

	useEffect(() => {
		return () => {
			if (localPreviewUrlRef.current) {
				URL.revokeObjectURL(localPreviewUrlRef.current)
			}
		}
	}, [])

	// Repopulate when the modal opens, and also when the persisted image URL
	// resolves/changes while open (e.g. async file data arrives). This mirrors
	// the Edit modals, whose populate effect listed the existing-URL as a dep
	// and re-seeded all image state whenever it changed.
	useEffect(() => {
		if (isOpen === undefined || !isOpen) return
		setImageFile(null)
		setLocalPreviewUrl(prev => {
			if (prev) URL.revokeObjectURL(prev)
			return null
		})
		setExistingUrl(existingImageUrl)
		setDeleteImage(false)
		setShowOverlay(false)
	}, [isOpen, existingImageUrl])

	const handleImageSelect = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
		const file = e.target.files?.[0]
		if (!file) return
		setImageFile(file)
		setLocalPreviewUrl(prev => {
			if (prev) URL.revokeObjectURL(prev)
			return URL.createObjectURL(file)
		})
		setDeleteImage(false)
	}, [])

	const handleImageRemove = useCallback(() => {
		setImageFile(null)
		setLocalPreviewUrl(prev => {
			if (prev) URL.revokeObjectURL(prev)
			return null
		})
		setExistingUrl(prev => {
			if (prev) setDeleteImage(true)
			return null
		})
		if (fileInputRef.current) fileInputRef.current.value = ''
	}, [])

	const previewUrl = localPreviewUrl ?? existingUrl

	return {
		imageFile,
		previewUrl,
		deleteImage,
		showOverlay,
		setShowOverlay,
		fileInputRef,
		handleImageSelect,
		handleImageRemove,
	}
}
